(function (window) {
  'use strict';

  // ─── Utilities ──────────────────────────────────────────────────────────────

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function getInstallation(rules, id) {
    return (
      rules.installations.find(function (item) {
        return item.id === id;
      }) || null
    );
  }

  function getFixture(rules, installationId, shapeId, fixtureId) {
    var installation = getInstallation(rules, installationId);
    if (!installation || !installation.fixturesByShape[shapeId]) return null;
    return (
      installation.fixturesByShape[shapeId].find(function (fixture) {
        return fixture.id === fixtureId;
      }) || null
    );
  }

  function toCartVariantId(value) {
    var text = String(value || '');
    var gidMatch = text.match(/^gid:\/\/shopify\/ProductVariant\/(\d+)$/);
    if (gidMatch) return gidMatch[1];
    return /^\d+$/.test(text) ? text : null;
  }

  function getCartVariantId(entry) {
    if (!entry) return null;
    return toCartVariantId(entry.cartVariantId || entry.variantId);
  }

  function cableColorShortLabel(colorId, color) {
    var byId = { black: 'Black', blue: 'Blue', yellow: 'Yellow', green: 'Green' };
    if (byId[colorId]) return byId[colorId];
    if (!color || !color.label) return colorId ? String(colorId) : '';
    // Rules labels are like "DGX-P-18 Black" — cart only needs the colour word.
    return String(color.label).replace(/^DGX-P-18\s+/i, '').trim() || String(color.label);
  }

  function cableDisplayLabel(rules, branch) {
    var color = (rules.colors || []).find(function (item) {
      return item.id === branch.colorId;
    });
    var meters = Number(branch.lengthMeters) || 0;
    var short = cableColorShortLabel(branch.colorId, color);
    if (!short || !meters) return '';
    return 'Color: ' + short + ' / Length: ' + meters + ' m';
  }

  function getCable(rules, colorId, lengthMeters) {
    if (!rules.sharedCableByColorAndLength[colorId]) return null;
    return rules.sharedCableByColorAndLength[colorId][lengthMeters] || null;
  }

  function totalFixtures(state) {
    return state.installations.reduce(function (sum, installation) {
      return (
        sum +
        Object.keys(installation.fixtures || {}).reduce(function (fixtureSum, id) {
          return fixtureSum + Number(installation.fixtures[id] || 0);
        }, 0)
      );
    }, 0);
  }

  // Slot weights toward the kit limit (physical cart qty stays 1:1).
  // YQPC15 = 2 slots; small PC pendants = 0.5 slot each.
  function fixtureSlotWeight(fixtureId) {
    var id = String(fixtureId || '');
    if (/YQPC15-13W/i.test(id)) return 2;
    if (/CQA-PC-D150-5W/i.test(id)) return 0.5;
    if (/CQA-PC-D200-5\.5W/i.test(id)) return 0.5;
    if (/CQA-PC-FB220-5\.5W/i.test(id)) return 0.5;
    return 1;
  }

  function roundFixtureSlots(value) {
    return Math.round(Number(value || 0) * 2) / 2;
  }

  function totalFixtureSlots(state) {
    var raw = state.installations.reduce(function (sum, installation) {
      return (
        sum +
        Object.keys(installation.fixtures || {}).reduce(function (fixtureSum, id) {
          return fixtureSum + Number(installation.fixtures[id] || 0) * fixtureSlotWeight(id);
        }, 0)
      );
    }, 0);
    return roundFixtureSlots(raw);
  }

  // ─── Cart item builders ─────────────────────────────────────────────────────

  function buildLegacyProperties(configurationId, branchId, installation, branch, state, component) {
    var properties = {
      'Configuration ID': configurationId,
      'Branch ID': branchId,
      Installation: installationCartLabel(installation),
      Shape: branch.shapeId,
      Color: branch.colorId,
      'Line length': branch.lengthMeters + 'm',
      'Lighting mode': state.lightingMode,
      Component: component,
      _itemKey: configurationId + '-' + branchId + '-' + component,
    };
    return properties;
  }

  function rulesFromInstallation(installation) {
    return installation && installation.rules ? installation.rules : { colors: [] };
  }

  function rulesColorValue(rules, colorId) {
    var color = (rules.colors || []).find(function (item) {
      return item.id === colorId;
    });
    return color ? color.value : null;
  }

  function expectedSplitter(rules, state) {
    var count = state.installations.length;
    // UI hides 直连 for one installation, but cart still bills the mapped 1-branch splitter when present.
    return rules.splittersByBranchCount[count] || null;
  }

  function listBillableLines(rules, state) {
    var lines = [];
    if (!state || !state.installations || !state.installations.length) return lines;
    var pricing = window.LEAVES_CONFIGURATOR_PRICING;
    var isMapped = pricing && pricing.entryIsMapped ? pricing.entryIsMapped : function (entry) {
      return !!(entry && (entry.cartVariantId || entry.variantId || entry.sku));
    };
    // Only bill accessories that are intentionally mapped. Never invent adapter/splitter IDs.
    if (rules.adapter && isMapped(rules.adapter)) {
      lines.push({
        key: 'adapter',
        label: rules.adapter.label || 'Adapter',
        quantity: 1,
        entry: rules.adapter,
        component: 'Adapter',
      });
    }
    var splitter = expectedSplitter(rules, state);
    if (splitter && state.installations.length >= 2 && isMapped(splitter)) {
      lines.push({
        key: 'splitter-' + state.installations.length,
        label: splitter.label || 'Splitter',
        quantity: 1,
        entry: splitter,
        component: splitter.label || 'Splitter',
      });
    } else if (splitter && state.installations.length === 1 && isMapped(splitter)) {
      lines.push({
        key: 'splitter-1',
        label: splitter.label || 'Direct connection',
        quantity: 1,
        entry: splitter,
        component: splitter.label || 'Direct connection',
      });
    }
    state.installations.forEach(function (branch, index) {
      var installation = getInstallation(rules, branch.installationId);
      var lineBranchId = 'branch-' + (index + 1);
      var token = branch.branchId || lineBranchId;
      var cable = getCable(rules, branch.colorId, Number(branch.lengthMeters));
      if (cable) {
        lines.push({
          key: 'cable-' + token,
          // Cart title = Cable; colour · length goes in _lineMeta (no DGX SKU prefix).
          label: 'Cable',
          meta: cableDisplayLabel(rules, branch) || '',
          quantity: 1,
          entry: cable,
          component: 'Shared cable',
          branchId: lineBranchId,
          branch: branch,
          installation: installation,
        });
      }
      Object.keys(branch.fixtures || {}).forEach(function (fixtureId) {
        var qty = Number(branch.fixtures[fixtureId] || 0);
        if (!qty) return;
        var fixture = getFixture(rules, branch.installationId, branch.shapeId, fixtureId);
        if (!fixture && installation && installation.tracks) {
          fixture = installation.tracks.find(function (track) {
            return track.id === fixtureId || track.sku === fixtureId;
          });
        }
        if (!fixture) return;
        lines.push({
          key: 'fixture-' + token + '-' + fixtureId,
          label: fixture.label || fixtureId,
          quantity: qty,
          entry: fixture,
          component: 'Fixture',
          branchId: lineBranchId,
          branch: branch,
          installation: installation,
        });
      });
      if (installation && installation.family === 'lnt') {
        var trackId =
          branch.trackId ||
          Object.keys(branch.fixtures || {}).filter(function (id) {
            return /^ZCHW-/i.test(id);
          })[0] ||
          '';
        if (
          trackId &&
          !Object.keys(branch.fixtures || {}).some(function (id) {
            return id === trackId;
          })
        ) {
          var track = (installation.tracks || []).find(function (item) {
            return item.id === trackId || item.sku === trackId;
          });
          if (track) {
            lines.push({
              key: 'track-' + token + '-' + trackId,
              label: track.label || trackId,
              quantity: 1,
              entry: track,
              component: 'LNT Track',
              branchId: lineBranchId,
              branch: branch,
              installation: installation,
            });
          }
        }
      }
    });
    return lines;
  }

  function resolveLineVariantId(line, catalog) {
    if (catalog && window.LEAVES_CONFIGURATOR_PRICING) {
      var resolved = window.LEAVES_CONFIGURATOR_PRICING.resolveCatalogEntry(line.entry, catalog);
      if (resolved) return resolved.id;
    }
    return getCartVariantId(line.entry);
  }

  function validateMappedEntry(entry, label, catalog) {
    if (catalog && window.LEAVES_CONFIGURATOR_PRICING) {
      var resolved = window.LEAVES_CONFIGURATOR_PRICING.resolveCatalogEntry(entry, catalog);
      if (resolved) return null;
    }
    if (getCartVariantId(entry)) return null;
    return label + ' variant is not mapped or could not be verified in Shopify.';
  }

  function createState() {
    return { sceneId: 'patio', installations: [], lightingMode: '', adapter: true, splitter: null };
  }

  function buildKitProperties(rules, state, configurationId) {
    var splitter = rules.splittersByBranchCount[state.installations.length];
    var properties = {
      _itemKey: configurationId,
      'Configuration ID': configurationId,
    };
    if (splitter) properties.Splitter = splitter.label || '';
    state.installations.forEach(function (branch, index) {
      var n = index + 1;
      var installation = getInstallation(rules, branch.installationId);
      properties['Length ' + n] = branch.lengthMeters + ' m';
      properties['Color ' + n] = colorLabel(rules, branch.colorId);
      properties['Installation ' + n] = installationCartLabel(installation);
      properties['Shape ' + n] = shapeLabel(rules, branch.shapeId);
      properties['Lighting ' + n] = state.lightingMode;
      properties['Fixtures ' + n] = Object.keys(branch.fixtures)
        .map(function (fixtureId) {
          return fixtureId + ' | ' + branch.fixtures[fixtureId];
        })
        .join(',');
    });
    return properties;
  }

  function buildComponentCartItems(rules, state, catalog, configurationId) {
    var items = [];
    var lines = listBillableLines(rules, state);
    for (var i = 0; i < lines.length; i += 1) {
      var line = lines[i];
      var variantId = resolveLineVariantId(line, catalog);
      if (!variantId) {
        return {
          valid: false,
          errors: [line.label + ' could not be resolved to a Shopify variant.'],
          items: [],
        };
      }
      var properties = {
        'Configuration ID': configurationId,
        Component: line.component,
        _parentKey: configurationId,
        _itemKey: configurationId + '-' + line.key,
      };
      if (line.branch && line.installation) {
        properties = Object.assign(
          {},
          buildLegacyProperties(
            configurationId,
            line.branchId,
            Object.assign({}, line.installation, { rules: rules }),
            line.branch,
            state,
            line.component
          ),
          {
            Component: line.component,
            _parentKey: configurationId,
            _itemKey: configurationId + '-' + line.key,
          },
          line.component === 'Fixture' ? { Fixture: line.label } : {}
        );
      }
      items.push({
        id: variantId,
        quantity: line.quantity,
        properties: properties,
      });
    }
    return { valid: true, errors: [], items: items };
  }

  function gateCartItems(items, catalog) {
    if (!catalog || !window.LEAVES_CONFIGURATOR_PRICING) {
      return { valid: true, errors: [], items: items };
    }
    var gate = window.LEAVES_CONFIGURATOR_PRICING.assertCartItemsSafe(items, catalog);
    if (!gate.valid) {
      var details = (gate.rejected || [])
        .map(function (code) {
          var unavailable = String(code || '').match(/^unavailable_variant:(\d+)$/);
          if (unavailable && catalog.byId && catalog.byId[unavailable[1]]) {
            var entry = catalog.byId[unavailable[1]];
            return (entry.productTitle || entry.title || unavailable[1]) + ' is out of stock';
          }
          var unknown = String(code || '').match(/^unknown_variant:(\d+)$/);
          if (unknown) return 'Unknown variant ' + unknown[1];
          return String(code || 'invalid_item');
        })
        .filter(Boolean);
      return {
        valid: false,
        errors: [
          details.length
            ? 'Add to cart blocked: ' + details.join('; ') + '.'
            : 'Add to cart blocked: one or more variants failed Shopify price catalog checks.',
        ],
        items: [],
        rejected: gate.rejected,
      };
    }
    return { valid: true, errors: [], items: gate.items };
  }

  function shortItemKey() {
    return Date.now().toString(36).slice(-5) + Math.random().toString(16).slice(2, 7);
  }

  function shapeLabel(rules, shapeId) {
    var shape = (rules.shapes || []).find(function (item) {
      return item.id === shapeId;
    });
    return shape ? shape.label : shapeId;
  }

  function installationCartLabel(installation) {
    if (!installation) return '';
    return installation.pluginLabel || installation.label || installation.id || '';
  }

  function tepoColorDisplay(rules, colorId) {
    var color = (rules.colors || []).find(function (item) {
      return item.id === colorId;
    });
    if (!color) return '';
    if (color.tepoDisplay) return String(color.tepoDisplay);
    // Product-page format: "DGX-P-18黑" (no space before the color character).
    return String(color.label || '').replace(/\s+/g, '');
  }

  function resolveInstallationVariantId(installation, catalog) {
    var handle = installation.handle || installation.productHandle;
    var mapped = getCartVariantId(installation);

    // Prefer the explicitly mapped parent variant when it exists and is sellable.
    // Only fall back to byHandle default when the mapped id is missing from catalog.
    if (catalog && catalog.loaded) {
      if (mapped && catalog.byId && catalog.byId[mapped]) {
        if (catalog.byId[mapped].available !== false) return mapped;
      }
      if (catalog.byHandle) {
        var fromHandle = handle ? catalog.byHandle[handle] : null;
        if (fromHandle && fromHandle.id && fromHandle.available !== false) return String(fromHandle.id);
        if (fromHandle && fromHandle.id) return String(fromHandle.id);
      }
      if (mapped && catalog.byId && catalog.byId[mapped]) return mapped;
      return null;
    }

    if (mapped) return mapped;
    if (!catalog || !catalog.byHandle) return null;
    var entry = handle ? catalog.byHandle[handle] : null;
    return entry && entry.id ? String(entry.id) : null;
  }

  /**
   * Match Tepo Product Options cart properties from live product pages.
   * Color Bridge and LNT use different option labels (confirmed on storefront forms).
   */
  function isTrackSku(sku) {
    return /^ZCHW-/i.test(String(sku || ''));
  }

  function findTrack(installation, trackId) {
    if (!installation || !trackId) return null;
    return (
      (installation.tracks || []).find(function (item) {
        return item.id === trackId || item.sku === trackId;
      }) || null
    );
  }

  function resolveTrackId(branch, installation) {
    if (branch.trackId) return String(branch.trackId);
    var fixtureIds = Object.keys(branch.fixtures || {});
    for (var i = 0; i < fixtureIds.length; i += 1) {
      if (isTrackSku(fixtureIds[i])) return String(fixtureIds[i]);
    }
    // Floor track must be an explicit size choice. Other LNT kits keep the first mapped track.
    if (installation && installation.trackChoice === 'required') return '';
    if (installation && installation.tracks && installation.tracks.length) {
      return String(installation.tracks[0].id || installation.tracks[0].sku || '');
    }
    return '';
  }

  function trackPropertyValue(branch, installation) {
    var trackId = resolveTrackId(branch, installation);
    var track = findTrack(installation, trackId);
    if (track && track.trackValue) return String(track.trackValue);
    return formatTrackValue(trackId);
  }

  function formatTrackValue(trackId) {
    var trackValue = String(trackId || '');
    if (/^ZCHW-/i.test(trackValue) && !/cm$/i.test(trackValue)) {
      trackValue = trackValue + 'cm';
    }
    return trackValue;
  }

  function fixturesCopyValue(branch, installation) {
    return Object.keys(branch.fixtures || {})
      .filter(function (fixtureId) {
        // Track SKUs are written on Track, not in the fixture list.
        if (installation && installation.family === 'lnt' && isTrackSku(fixtureId)) return false;
        return Number(branch.fixtures[fixtureId] || 0) > 0;
      })
      .map(function (fixtureId) {
        return fixtureId + ' | ' + branch.fixtures[fixtureId];
      })
      .join(', ');
  }

  // ─── Editable state (serialize / parse / apply) ─────────────────────────────

  function serializeEditableState(state) {
    return {
      v: 1,
      lightingMode: state.lightingMode || '',
      installations: (state.installations || []).map(function (branch) {
        return {
          installationId: branch.installationId,
          lengthMeters: Number(branch.lengthMeters) || 0,
          shapeId: branch.shapeId || '',
          colorId: branch.colorId || '',
          fixtures: Object.assign({}, branch.fixtures || {}),
          trackId: branch.trackId || '',
        };
      }),
    };
  }

  function parseEditableState(raw) {
    if (!raw) return null;
    var data = raw;
    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw);
      } catch (error) {
        return null;
      }
    }
    if (!data || Number(data.v) !== 1 || !Array.isArray(data.installations)) return null;
    return data;
  }

  function applyEditableState(targetState, saved) {
    if (!targetState || !saved) return false;
    targetState.lightingMode = saved.lightingMode || '';
    targetState.installations = (saved.installations || []).map(function (branch, index) {
      return {
        installationId: branch.installationId,
        branchId: 'branch-' + (index + 1),
        lengthMeters: Number(branch.lengthMeters) || 0,
        shapeId: branch.shapeId || '',
        colorId: branch.colorId || '',
        fixtures: Object.assign({}, branch.fixtures || {}),
        trackId: branch.trackId || '',
      };
    });
    return true;
  }

  function attachLeavesEditMeta(properties, state, itemKey) {
    var next = properties || {};
    next._itemKey = itemKey || next._itemKey;
    next._leavesKit = 'patio';
    next._leavesState = JSON.stringify(serializeEditableState(state));
    if (Object.prototype.hasOwnProperty.call(next, '_optionSetId')) delete next._optionSetId;
    return next;
  }

  function colorLabel(rules, colorId) {
    var color = (rules.colors || []).find(function (item) {
      return item.id === colorId;
    });
    return color ? String(color.label || '') : '';
  }

  function buildTepoProperties(rules, state, branch, installation, itemKey) {
    var fixturesCopy = fixturesCopyValue(branch, installation);
    var isLnt = installation.family === 'lnt';
    var properties = {
      Installation: installationCartLabel(installation),
      Shape: shapeLabel(rules, branch.shapeId),
      Color: colorLabel(rules, branch.colorId),
      Length: branch.lengthMeters + ' m',
      _leavesInstall1: installation.label || installationCartLabel(installation),
      _itemKey: itemKey,
    };

    if (isLnt) {
      var trackText = trackPropertyValue(branch, installation);
      if (trackText) properties.Track = trackText;
    }
    if (fixturesCopy) properties.Fixtures = fixturesCopy;

    return attachLeavesEditMeta(properties, state, itemKey);
  }

  function listBillableLinesForBranch(rules, state, branch, index) {
    var all = listBillableLines(rules, state);
    var branchId = 'branch-' + (index + 1);
    return all.filter(function (line) {
      return line.branchId === branchId;
    });
  }

  function getMultipleLightingPack(rules, branchCount) {
    var packs = rules.multipleLightingByBranchCount || {};
    return packs[branchCount] || null;
  }

  function resolveMultipleLightingVariantId(pack, catalog) {
    if (!pack) return null;
    var handle = pack.productHandle;
    var mapped = getCartVariantId(pack);
    if (catalog && catalog.loaded) {
      if (mapped && catalog.byId && catalog.byId[mapped]) {
        if (catalog.byId[mapped].available !== false) return mapped;
      }
      if (catalog.byHandle) {
        var fromHandle = handle ? catalog.byHandle[handle] : null;
        if (fromHandle && fromHandle.id && fromHandle.available !== false) return String(fromHandle.id);
        if (fromHandle && fromHandle.id) return String(fromHandle.id);
      }
      if (mapped && catalog.byId && catalog.byId[mapped]) return mapped;
      return null;
    }
    if (mapped) return mapped;
    if (!catalog || !catalog.byHandle) return null;
    var entry = handle ? catalog.byHandle[handle] : null;
    return entry && entry.id ? String(entry.id) : null;
  }

  function resolveMultipleLightingPack(rules, state, catalog) {
    var count = state.installations.length;
    var pack = getMultipleLightingPack(rules, count);
    if (!pack) return null;
    var parentId = resolveMultipleLightingVariantId(pack, catalog);
    if (catalog && catalog.loaded && parentId && catalog.byId[parentId] && catalog.byId[parentId].available === false) {
      // 2-set product is currently sold out on storefront; fall back to 3-set parent
      // while still only writing the selected branch fields (safe + sellable).
      if (count === 2) {
        var threePack = getMultipleLightingPack(rules, 3);
        var threeId = resolveMultipleLightingVariantId(threePack, catalog);
        if (threeId && catalog.byId[threeId] && catalog.byId[threeId].available !== false) {
          return threePack;
        }
      }
    }
    return pack;
  }

  function buildTepoMultipleProperties(rules, state, itemKey) {
    var properties = {
      _itemKey: itemKey,
    };
    var lightingParts = [];
    var splitter = expectedSplitter(rules, state);
    if (splitter && state.installations.length >= 2) {
      properties.Splitter = splitter.label || '';
      properties._leavesSplitter = splitter.label || '';
    }

    state.installations.forEach(function (branch, index) {
      var installation = getInstallation(rules, branch.installationId);
      var n = index + 1;
      properties['Color ' + n] = colorLabel(rules, branch.colorId);
      properties['Length ' + n] = branch.lengthMeters + ' m';
      if (installation) {
        properties['Installation ' + n] = installationCartLabel(installation);
        properties['Shape ' + n] = shapeLabel(rules, branch.shapeId);
        properties['_leavesInstall' + n] = installation.label || installationCartLabel(installation);
      }
      Object.keys(branch.fixtures || {}).forEach(function (fixtureId) {
        var qty = Number(branch.fixtures[fixtureId] || 0);
        if (!qty) return;
        if (installation && installation.family === 'lnt' && isTrackSku(fixtureId)) return;
        lightingParts.push(fixtureId + ' | ' + qty);
      });
    });
    if (lightingParts.length) properties.Fixtures = lightingParts.join(', ');
    return attachLeavesEditMeta(properties, state, itemKey);
  }

  function resolveLineImage(line, catalog, variantId) {
    var fromEntry = line && line.entry && (line.entry.imageUrl || line.entry.image);
    if (fromEntry) return String(fromEntry);
    if (line && line.installation && line.installation.imageUrl) {
      return String(line.installation.imageUrl);
    }
    if (variantId && catalog && catalog.byId && catalog.byId[variantId] && catalog.byId[variantId].imageUrl) {
      return String(catalog.byId[variantId].imageUrl);
    }
    return '';
  }

  function buildAddonItemsFromLines(lines, catalog, parentId, itemKey) {
    var addOnParts = [];
    var addonItems = [];
    for (var j = 0; j < lines.length; j += 1) {
      var line = lines[j];
      var variantId = resolveLineVariantId(line, catalog);
      if (!variantId) {
        return {
          valid: false,
          errors: [line.label + ' could not be resolved to a Shopify variant.'],
          addOnParts: [],
          addonItems: [],
        };
      }
      var qty = Math.max(1, Math.floor(Number(line.quantity) || 1));
      addOnParts.push(String(variantId) + ':' + qty + ':0');
      var addonProps = {
        _parentKey: itemKey,
        _variantId: String(variantId),
        // Per-kit quantity so cart +/- can scale the whole configuration.
        _kitUnitQty: String(qty),
      };
      if (line.label) addonProps._lineLabel = String(line.label);
      if (line.meta) addonProps._lineMeta = String(line.meta);
      var lineKind = resolveLineKind(line);
      if (lineKind) addonProps._lineKind = lineKind;
      var lineImage = resolveLineImage(line, catalog, variantId);
      if (lineImage) addonProps._lineImage = String(lineImage);
      if (line.installation && line.installation.label) {
        addonProps._installLabel = String(line.installation.label);
      }
      if (line.branchId) {
        var branchMatch = String(line.branchId).match(/(\d+)$/);
        if (branchMatch) addonProps._installIndex = branchMatch[1];
      }
      var addonLine = {
        id: variantId,
        quantity: qty,
        properties: addonProps,
      };
      if (parentId) addonLine.parent_id = parentId;
      addonItems.push(addonLine);
    }
    return { valid: true, errors: [], addOnParts: addOnParts, addonItems: addonItems };
  }

  function resolveLineKind(line) {
    if (!line) return '';
    var key = String(line.key || '');
    if (key.indexOf('fixture-') === 0 || line.component === 'Fixture') return 'fixture';
    if (key.indexOf('cable-') === 0 || line.component === 'Shared cable') return 'cable';
    if (key.indexOf('adapter') === 0 || line.component === 'Adapter') return 'adapter';
    if (key.indexOf('splitter') === 0) return 'splitter';
    if (key.indexOf('track-') === 0 || line.component === 'LNT Track') return 'track';
    return '';
  }

  function isVariantSellable(catalog, variantId) {
    if (!variantId) return false;
    if (!catalog || !catalog.loaded || !catalog.byId) return true;
    var entry = catalog.byId[String(variantId)];
    return !!(entry && entry.available !== false);
  }

  // When the €0 installation / multiple-lighting parent is sold out in Shopify,
  // keep checkout working by promoting the first sellable component to kit host.
  // Theme grouping still uses _itemKey / _parentKey (Shopify parent_id is optional).
  function buildFlatKitItems(addonItems, kitProperties) {
    if (!addonItems || !addonItems.length) {
      return {
        valid: false,
        errors: ['Kit is missing sellable components, so add to cart is blocked.'],
        items: [],
      };
    }
    var hostIndex = 0;
    for (var i = 0; i < addonItems.length; i += 1) {
      var kind = addonItems[i].properties && addonItems[i].properties._lineKind;
      if (kind === 'fixture') {
        hostIndex = i;
        break;
      }
    }
    var host = addonItems[hostIndex];
    var hostProps = Object.assign({}, host.properties || {}, kitProperties || {});
    delete hostProps._parentKey;
    var itemKey = (kitProperties && kitProperties._itemKey) || hostProps._itemKey;
    var items = [
      {
        id: host.id,
        quantity: host.quantity,
        properties: hostProps,
      },
    ];
    for (var j = 0; j < addonItems.length; j += 1) {
      if (j === hostIndex) continue;
      var child = addonItems[j];
      var childProps = Object.assign({}, child.properties || {});
      if (itemKey) childProps._parentKey = itemKey;
      items.push({
        id: child.id,
        quantity: child.quantity,
        properties: childProps,
      });
    }
    return { valid: true, errors: [], items: items };
  }

  function buildTepoMultipleLightingCartItems(rules, state, catalog) {
    var pack = resolveMultipleLightingPack(rules, state, catalog);
    if (!pack) {
      return {
        valid: false,
        errors: ['Multiple lighting parent product is not configured (need 2-set / 3-set products).'],
        items: [],
      };
    }
    var parentId = resolveMultipleLightingVariantId(pack, catalog);
    var parentSellable = isVariantSellable(catalog, parentId);

    var itemKey = shortItemKey();
    var billable = listBillableLines(rules, state).filter(function (line) {
      return !!line.branchId;
    });
    var nestParentId = parentSellable ? parentId : null;
    var built = buildAddonItemsFromLines(billable, catalog, nestParentId, itemKey);
    if (!built.valid) return { valid: false, errors: built.errors, items: [] };
    if (!built.addonItems.length) {
      return {
        valid: false,
        errors: ['Kit is missing priced components, so a non-zero cart total cannot be created.'],
        items: [],
      };
    }

    var properties = buildTepoMultipleProperties(rules, state, itemKey);
    if (parentSellable) {
      return {
        valid: true,
        errors: [],
        items: [
          {
            id: parentId,
            quantity: 1,
            properties: properties,
          },
        ].concat(built.addonItems),
      };
    }
    var flatAddons = built.addonItems.map(function (item) {
      return {
        id: item.id,
        quantity: item.quantity,
        properties: item.properties,
      };
    });
    return buildFlatKitItems(flatAddons, properties);
  }

  function buildTepoInstallationCartItems(rules, state, catalog) {
    // 2/3 installations → one Multiple lighting parent (product-page pattern).
    if (state.installations.length >= 2) {
      return buildTepoMultipleLightingCartItems(rules, state, catalog);
    }

    var items = [];
    for (var i = 0; i < state.installations.length; i += 1) {
      var branch = state.installations[i];
      var installation = getInstallation(rules, branch.installationId);
      if (!installation) {
        return { valid: false, errors: ['Unknown installation method.'], items: [] };
      }
      var parentId = resolveInstallationVariantId(installation, catalog);
      var parentSellable = isVariantSellable(catalog, parentId);
      if (!parentId && !parentSellable) {
        return {
          valid: false,
          errors: [
            installation.label +
              ' could not resolve a Shopify product variant (handle: ' +
              (installation.handle || '') +
              '）。',
          ],
          items: [],
        };
      }
      if (installation.family === 'lnt' && !resolveTrackId(branch, installation)) {
        return {
          valid: false,
          errors: [installation.label + ' is missing an LNT Track option and cannot be added as a product-page kit.'],
          items: [],
        };
      }

      var itemKey = shortItemKey();
      var branchLines = listBillableLinesForBranch(rules, state, branch, i);
      var addonItems = [];
      for (var j = 0; j < branchLines.length; j += 1) {
        var line = branchLines[j];
        var variantId = resolveLineVariantId(line, catalog);
        if (!variantId) {
          return {
            valid: false,
            errors: [line.label + ' could not be resolved to a Shopify variant.'],
            items: [],
          };
        }
        if (!isVariantSellable(catalog, variantId)) {
          return {
            valid: false,
            errors: [line.label + ' is currently unavailable. Add to cart blocked.'],
            items: [],
          };
        }
        var qty = Math.max(1, Math.floor(Number(line.quantity) || 1));
        var singleAddonProps = {
          _parentKey: itemKey,
          _variantId: String(variantId),
          _kitUnitQty: String(qty),
        };
        if (line.label) singleAddonProps._lineLabel = String(line.label);
        if (line.meta) singleAddonProps._lineMeta = String(line.meta);
        var singleLineKind = resolveLineKind(line);
        if (singleLineKind) singleAddonProps._lineKind = singleLineKind;
        var singleLineImage = resolveLineImage(
          {
            entry: line.entry,
            installation: installation || line.installation,
          },
          catalog,
          variantId
        );
        if (singleLineImage) singleAddonProps._lineImage = String(singleLineImage);
        if (installation && installation.label) {
          singleAddonProps._installLabel = String(installation.label);
        }
        singleAddonProps._installIndex = String(i + 1);
        var addonLine = {
          id: variantId,
          quantity: qty,
          properties: singleAddonProps,
        };
        // Only nest under Shopify parent when that €0 shell is actually sellable.
        if (parentSellable) addonLine.parent_id = parentId;
        addonItems.push(addonLine);
      }

      var properties = buildTepoProperties(rules, state, branch, installation, itemKey);

      if (parentSellable) {
        items.push({
          id: parentId,
          quantity: 1,
          properties: properties,
        });
        for (var k = 0; k < addonItems.length; k += 1) items.push(addonItems[k]);
      } else {
        var flat = buildFlatKitItems(addonItems, properties);
        if (!flat.valid) return flat;
        for (var f = 0; f < flat.items.length; f += 1) items.push(flat.items[f]);
      }
    }
    if (!items.length) {
      return { valid: false, errors: ['No installation configuration is ready for the cart.'], items: [] };
    }
    return { valid: true, errors: [], items: items };
  }

  // ─── Validation & public buildCartItems / buildPriceQuote ───────────────────

  function validateState(rules, state, catalog) {
    var errors = [];
    if (!rules || rules.status !== 'validated' || rules.cartEnabled !== true)
      errors.push('Rules are not validated yet, so add to cart is disabled.');
    if (!state.installations.length) errors.push('Please select at least one installation method.');
    if (state.installations.length > rules.maxInstallations)
      errors.push('You can select up to ' + rules.maxInstallations + ' installation methods.');
    if (!['rgb', 'cw'].includes(state.lightingMode)) errors.push('Please choose a lighting mode.');
    var splitter = expectedSplitter(rules, state);
    if (!splitter) errors.push('Splitter accessory does not match the number of branches.');
    var total = totalFixtures(state);
    var slots = totalFixtureSlots(state);
    if (total < 1) errors.push('Please select at least one fixture.');
    if (slots > rules.maxTotalFixtures)
      errors.push('This patio kit allows up to ' + rules.maxTotalFixtures + ' fixtures in total.');

    var needsKit = rules.cartMode === 'single_configurator' || rules.cartMode === 'kit_with_components';
    // Bill cable, fixture, and track as real Shopify variants nested under the kit.
    var needsComponents =
      rules.cartMode === 'components' ||
      rules.cartMode === 'kit_with_components' ||
      rules.cartMode === 'tepo_installations';
    var needsInstallationProduct =
      rules.cartMode === 'tepo_installations' && state.installations.length === 1;
    var needsMultipleLightingParent =
      rules.cartMode === 'tepo_installations' && state.installations.length >= 2;
    var pricingApi = window.LEAVES_CONFIGURATOR_PRICING;
    var isMapped = pricingApi && pricingApi.entryIsMapped
      ? pricingApi.entryIsMapped
      : function (entry) {
          return !!(entry && (entry.cartVariantId || entry.variantId || entry.sku));
        };

    if (needsKit && !getCartVariantId(rules.configuratorVariant)) {
      errors.push('Configurator product variant is not mapped yet.');
    }

    if (needsMultipleLightingParent) {
      var multiPack = resolveMultipleLightingPack(rules, state, catalog);
      if (!multiPack) {
        errors.push('Multiple lighting parent product is not configured.');
      } else if (catalog && catalog.loaded && !resolveMultipleLightingVariantId(multiPack, catalog)) {
        errors.push(
          (multiPack.label || 'Multiple lighting') + ' product variant has not synced from Shopify.'
        );
      }
    }

    if (needsComponents) {
      if (isMapped(rules.adapter)) {
        var adapterError = validateMappedEntry(rules.adapter, 'Adapter', catalog);
        if (adapterError) errors.push(adapterError);
      }
      if (state.installations.length >= 2 && isMapped(splitter)) {
        var splitterError = validateMappedEntry(splitter, 'Splitter', catalog);
        if (splitterError) errors.push(splitterError);
      }
    }

    state.installations.forEach(function (branch) {
      var installation = getInstallation(rules, branch.installationId);
      if (!installation) {
        errors.push('Unknown installation method.');
        return;
      }
      if (needsInstallationProduct && catalog && catalog.loaded) {
        if (!resolveInstallationVariantId(installation, catalog)) {
          errors.push(installation.label + ' product variant has not synced from Shopify.');
        }
      }
      if (!installation.allowedLengths.includes(Number(branch.lengthMeters)))
        errors.push(branch.installationId + ': invalid cable length.');
      if (!rules.colors.some(function (color) {
        return color.id === branch.colorId;
      }))
        errors.push(branch.installationId + ': invalid cable color.');
      if (needsComponents) {
        var cable = getCable(rules, branch.colorId, Number(branch.lengthMeters));
        var cableError = validateMappedEntry(cable, branch.installationId + ' color and length', catalog);
        if (cableError) errors.push(cableError);
      }
      if (!branch.shapeId || !installation.fixturesByShape[branch.shapeId])
        errors.push(branch.installationId + ': invalid shape.');
      if (installation.trackChoice === 'required' && !findTrack(installation, branch.trackId)) {
        errors.push((installation.label || branch.installationId) + ': choose one ZCHW-F-P-800 size.');
      }
      if (!branch.fixtures || !Object.keys(branch.fixtures).length)
        errors.push(branch.installationId + ': select at least one fixture.');
      Object.keys(branch.fixtures || {}).forEach(function (fixtureId) {
        var fixture = getFixture(rules, branch.installationId, branch.shapeId, fixtureId);
        var quantity = Number(branch.fixtures[fixtureId]);
        if (!fixture) {
          errors.push(branch.installationId + ': invalid fixture.');
        } else if (needsComponents) {
          var fixtureError = validateMappedEntry(fixture, branch.installationId + ' fixture', catalog);
          if (fixtureError) errors.push(fixtureError);
        }
        if (fixture && (!fixture.allowedLightingModes || !fixture.allowedLightingModes.includes(state.lightingMode)))
          errors.push(branch.installationId + ': fixture does not match the lighting mode.');
        if (!Number.isInteger(quantity) || quantity < 1) errors.push('Fixture quantity must be a positive integer.');
      });
    });
    return { valid: errors.length === 0, errors: errors };
  }

  function buildCartItems(rules, state, catalog) {
    var validation = validateState(rules, state, catalog);
    if (!validation.valid) return { valid: false, errors: validation.errors, items: [] };
    var needsCatalogGate =
      rules.cartMode === 'components' ||
      rules.cartMode === 'kit_with_components' ||
      rules.cartMode === 'tepo_installations';
    if (needsCatalogGate) {
      if (!catalog || !catalog.loaded || !window.LEAVES_CONFIGURATOR_PRICING) {
        return {
          valid: false,
          errors: ['Security check: Shopify price catalog is not loaded. Add to cart blocked.'],
          items: [],
        };
      }
    }
    var configurationId = 'PAT-' + Date.now().toString(36).toUpperCase();

    if (rules.cartMode === 'tepo_installations') {
      var tepoResult = buildTepoInstallationCartItems(rules, state, catalog);
      if (!tepoResult.valid) return tepoResult;
      return gateCartItems(tepoResult.items, catalog);
    }

    if (rules.cartMode === 'single_configurator') {
      var kitIdOnly = getCartVariantId(rules.configuratorVariant);
      if (!kitIdOnly) {
        return { valid: false, errors: ['Configurator product variant is not mapped yet.'], items: [] };
      }
      var kitOnlyItems = [
        {
          id: kitIdOnly,
          quantity: 1,
          properties: buildKitProperties(rules, state, configurationId),
        },
      ];
      if (catalog && catalog.loaded && catalog.byId && catalog.byId[kitIdOnly]) {
        if (catalog.byId[kitIdOnly].available === false) {
          return { valid: false, errors: ['Configurator product is currently unavailable. Add to cart blocked.'], items: [] };
        }
        return gateCartItems(kitOnlyItems, catalog);
      }
      return { valid: true, errors: [], items: kitOnlyItems };
    }

    if (rules.cartMode === 'kit_with_components') {
      var kitId = getCartVariantId(rules.configuratorVariant);
      if (!kitId) {
        return { valid: false, errors: ['Configurator product variant is not mapped yet.'], items: [] };
      }
      var componentResult = buildComponentCartItems(rules, state, catalog, configurationId);
      if (!componentResult.valid) return componentResult;
      if (!componentResult.items.length) {
        return {
          valid: false,
          errors: ['Kit is missing priced components, so a non-zero cart total cannot be created.'],
          items: [],
        };
      }
      var kitProperties = buildKitProperties(rules, state, configurationId);
      var nestedComponents = componentResult.items.map(function (item) {
        return {
          id: item.id,
          quantity: item.quantity,
          parent_id: kitId,
          properties: item.properties || {},
        };
      });
      var bundled = [
        {
          id: kitId,
          quantity: 1,
          properties: kitProperties,
        },
      ].concat(nestedComponents);

      var catalogForGate = catalog;
      if (catalog && !catalog.byId[kitId]) {
        var byId = {};
        Object.keys(catalog.byId || {}).forEach(function (key) {
          byId[key] = catalog.byId[key];
        });
        byId[kitId] = {
          id: kitId,
          priceCents: 0,
          available: true,
          sku: '',
          productHandle: rules.configuratorVariant.productHandle || '',
        };
        catalogForGate = {
          loaded: catalog.loaded,
          byId: byId,
          bySku: catalog.bySku || {},
          byStyle: catalog.byStyle || {},
          byCableKey: catalog.byCableKey || {},
          byHandle: catalog.byHandle || {},
        };
      }
      return gateCartItems(bundled, catalogForGate);
    }

    var componentOnly = buildComponentCartItems(rules, state, catalog, configurationId);
    if (!componentOnly.valid) return componentOnly;
    return gateCartItems(componentOnly.items, catalog);
  }

  function buildPriceQuote(rules, state, catalog) {
    var lines = listBillableLines(rules, state);
    if (!catalog || !window.LEAVES_CONFIGURATOR_PRICING) {
      return {
        lines: lines.map(function (line) {
          return {
            key: line.key,
            label: line.label,
            quantity: line.quantity,
            priced: false,
            priceCents: null,
            lineCents: null,
          };
        }),
        totalCents: 0,
        totalMoney: '',
        complete: false,
        missing: lines.map(function (line) {
          return line.label;
        }),
        unavailable: [],
        catalogLoaded: false,
      };
    }
    return window.LEAVES_CONFIGURATOR_PRICING.quoteLines(lines, catalog);
  }

  window.LEAVES_CONFIGURATOR_CORE = {
    clone: clone,
    toCartVariantId: toCartVariantId,
    createState: createState,
    getInstallation: getInstallation,
    getCable: getCable,
    cableDisplayLabel: cableDisplayLabel,
    getCartVariantId: getCartVariantId,
    totalFixtures: totalFixtures,
    fixtureSlotWeight: fixtureSlotWeight,
    roundFixtureSlots: roundFixtureSlots,
    totalFixtureSlots: totalFixtureSlots,
    listBillableLines: listBillableLines,
    validateState: validateState,
    buildCartItems: buildCartItems,
    buildPriceQuote: buildPriceQuote,
    serializeEditableState: serializeEditableState,
    parseEditableState: parseEditableState,
    applyEditableState: applyEditableState,
  };
})(window);
